'use client';
/**
 * ACCÈS AUDITEUR EXTERNE — le registre, côté laboratoire (MAN-9).
 *
 * ── Ce que cet écran doit rendre évident ───────────────────────────────────
 *
 * Ouvrir un accès est facile ; c'est le REFERMER qu'on oublie. L'écran est donc
 * bâti autour de la révocation : les accès actifs sont en tête, avec le nombre
 * de jours qui restent et le bouton pour retirer, sans avoir à ouvrir quoi que
 * ce soit.
 *
 * ⚠️ LE LIEN N'EST MONTRÉ QU'UNE FOIS, À LA CRÉATION. Il n'est pas stocké et
 * l'écran ne sait pas le reconstituer. Ce n'est pas une limite technique
 * subie : un lien qu'on peut re-afficher indéfiniment est un lien qui traîne
 * dans une capture d'écran, et le retrouver « au cas où » remplace la seule
 * bonne réaction — en ouvrir un neuf et révoquer l'ancien.
 *
 * ⚠️ ET LA RÉVOCATION DEMANDE UN MOTIF. Il restera au journal : « pourquoi
 * l'accès de l'auditeur a-t-il été coupé le 3 ? » est exactement le genre de
 * question qu'on se pose six mois plus tard, sans personne pour y répondre.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { I, card, inputStyle, EmptyState, Modale } from '@/components/ui';

import { Bouton } from '@/shared/Bouton';
import { fmtDate } from '@/lib/meta';
type Acces = {
  documentId: string; ref: string; intitule: string; organisme?: string; auditRef?: string;
  documents: string[]; expireLe: string; creePar?: string; creeLe?: string;
  revoqueLe?: string; revoquePar?: string; motifRevocation?: string;
  ouvertures: number; dernierAcces?: string;
  etat: 'actif' | 'revoque' | 'expire'; joursRestants: number | null;
};

const jour = (iso?: string | null) => fmtDate(iso);

const ETATS = {
  actif: { label: 'Ouvert', fg: 'var(--green-600)', bg: '#E7F5EC' },
  revoque: { label: 'Retiré', fg: 'var(--red-600)', bg: 'var(--red-50, #fdecec)' },
  expire: { label: 'Expiré', fg: 'var(--text-muted)', bg: 'var(--gray-25)' },
} as const;

export function AccesAudits() {
  const { docs, interroger } = useGed();
  const [liste, setListe] = React.useState<Acces[] | null>(null);
  const [creation, setCreation] = React.useState(false);
  const [lienNeuf, setLienNeuf] = React.useState<{ ref: string; lien: string } | null>(null);
  const [aRevoquer, setARevoquer] = React.useState<Acces | null>(null);

  const recharger = React.useCallback(async () => {
    const r = await interroger<{ acces?: Acces[] }>('ged/acces-audits', undefined);
    setListe(r?.acces || []);
  }, [interroger]);
  /* ⚠️ FAUX POSITIF ASSUMÉ de `react-hooks/set-state-in-effect` (02/09/2026).
     `charger` est un `useCallback` ASYNC : son `setState` se produit APRÈS un
     `await`, donc dans une continuation, jamais dans le corps de l'effet. La
     règle ne franchit pas la frontière asynchrone et l'annonce quand même.
     C'est l'usage canonique d'un effet — « s'abonner à un système externe ».
     ⚠️ Le silence est CIBLÉ, ligne à ligne : désactiver la règle pour le
     fichier masquerait un vrai cas ajouté demain. */
  // eslint-disable-next-line react-hooks/set-state-in-effect
  React.useEffect(() => { void recharger(); }, [recharger]);

  // Les OUVERTS d'abord : ce sont les seuls sur lesquels on peut encore agir.
  const triee = React.useMemo(() => {
    const rang = { actif: 0, expire: 1, revoque: 2 } as const;
    return [...(liste || [])].sort((a, b) => rang[a.etat] - rang[b.etat]
      || String(b.creeLe || '').localeCompare(String(a.creeLe || '')));
  }, [liste]);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <p style={{ margin: 0, flex: '1 1 320px', fontSize: 13, lineHeight: 1.6, color: 'var(--text-muted)' }}>
          Un lien de consultation remis à un auditeur externe : lecture seule, borné à une liste de
          documents et à une date. Chaque ouverture est inscrite au journal.
        </p>
        <Bouton variante="primaire" onClick={() => setCreation(true)}>
          <I n="plus" s={14} />Ouvrir un accès
        </Bouton>
      </div>

      {liste === null && <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Chargement…</p>}

      {liste !== null && triee.length === 0 && (
        <EmptyState
          titre="Aucun accès auditeur ouvert."
          indice="Un accès se crée au moment de l’audit et se retire dès qu’il est terminé." />
      )}

      {triee.map((a) => {
        const meta = ETATS[a.etat];
        return (
          <div key={a.documentId} style={{ ...card, padding: '13px 16px', display: 'grid', gap: 7 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)' }}>{a.ref}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>{a.intitule}</span>
              <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: meta.bg, color: meta.fg }}>
                {meta.label}
                {/* ⚠️ « Expire demain » se dit ACTIF avec un nombre de jours, et
                    non comme un quatrième état : il est encore ouvert
                    aujourd'hui, et le lire « bientôt fermé » ferait croire qu'il
                    n'y a plus rien à faire. */}
                {a.etat === 'actif' && a.joursRestants !== null ? ` · ${a.joursRestants} j` : ''}
              </span>
              <span style={{ flex: 1 }} />
              {a.etat === 'actif' && (
                <Bouton taille="sm" onClick={() => setARevoquer(a)}>
                  <I n="x" s={12} />Retirer l’accès
                </Bouton>
              )}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
              {a.organisme ? `${a.organisme} · ` : ''}
              {a.documents.length} document(s) · jusqu’au {jour(a.expireLe)}
              {' · '}
              {a.ouvertures > 0
                ? `${a.ouvertures} consultation(s), la dernière le ${jour(a.dernierAcces)}`
                : 'jamais ouvert'}
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>
              {a.documents.join(' · ')}
            </div>
            {a.revoqueLe && (
              <div style={{ fontSize: 12.5, color: 'var(--red-600)', lineHeight: 1.55 }}>
                Retiré le {jour(a.revoqueLe)} — {a.motifRevocation}
              </div>
            )}
          </div>
        );
      })}

      {creation && (
        <FormulaireAcces
          documents={docs.map((d) => ({ code: d.code, titre: d.titre }))}
          onFerme={() => setCreation(false)}
          onCree={(r) => { setCreation(false); setLienNeuf(r); void recharger(); }} />
      )}

      {/* ⚠️ PAS de `sansSaisie` sur la fenêtre ci-dessous, alors qu'elle ne fait
          qu'AFFICHER : le lien de l'auditeur « ne sera plus affiché ». Un clic à
          côté le perdrait pour de bon — c'est exactement ce que la règle du
          04/09/2026 empêche. Le confort consultatif ne vaut que là où rien ne se
          perd. */}
      {lienNeuf && (
        <Modale titre="Accès ouvert" sousTitre={lienNeuf.ref} onClose={() => setLienNeuf(null)} largeur={640}
          pied={<Bouton variante="primaire" onClick={() => setLienNeuf(null)}>J’ai copié le lien</Bouton>}>
          <div style={{ display: 'grid', gap: 12 }}>
            {/* ⚠️ Dit AVANT le lien, pas après : lu après, on a déjà fermé. */}
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-strong)' }}>
              <strong>Ce lien ne sera plus affiché.</strong> Copiez-le maintenant et transmettez-le à
              l’auditeur. S’il se perd, ouvrez-en un nouveau et retirez celui-ci.
            </p>
            <textarea readOnly value={lienNeuf.lien} rows={3}
              onFocus={(e) => e.currentTarget.select()}
              style={{ ...inputStyle, height: 'auto', padding: '9px 12px', fontFamily: 'var(--font-mono)', fontSize: 12.5, resize: 'vertical' }} />
          </div>
        </Modale>
      )}

      {aRevoquer && (
        <FormulaireRevocation acces={aRevoquer}
          onFerme={() => setARevoquer(null)}
          onFait={() => { setARevoquer(null); void recharger(); }} />
      )}
    </div>
  );
}

function FormulaireRevocation({ acces, onFerme, onFait }: {
  acces: Acces; onFerme: () => void; onFait: () => void;
}) {
  const { smqData: envoyer, showToast: signaler } = useGed();
  const [motif, setMotif] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);
  return (
    <Modale titre="Retirer cet accès" sousTitre={`${acces.ref} — ${acces.intitule}`} onClose={onFerme} largeur={520}
      pied={(
        <>
          <Bouton taille="sm" onClick={onFerme}>Annuler</Bouton>
          <Bouton variante="primaire" taille="sm" disabled={!motif.trim() || enCours} onClick={() => void (async () => {
              setEnCours(true);
              const r = await envoyer(`ged/acces-audits/${acces.documentId}/revoquer`, { motif: motif.trim() });
              setEnCours(false);
              if (!r) return;
              signaler('Accès retiré — le lien ne donne plus rien.');
              onFait();
            })()}>
            {enCours ? 'Retrait…' : 'Retirer'}
          </Bouton>
        </>
      )}>
      <div style={{ display: 'grid', gap: 10 }}>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6 }}>
          Le lien cessera de donner accès immédiatement. On ne rouvre pas un accès retiré : si
          l’auditeur en a encore besoin, ouvrez-en un nouveau.
        </p>
        <div>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5 }}>
            Motif du retrait
          </label>
          <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={2}
            placeholder="Audit terminé le 12/09."
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
          <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
            Il restera au journal — c’est ce qui répondra, dans six mois, à « pourquoi cet accès a-t-il été coupé ? ».
          </p>
        </div>
      </div>
    </Modale>
  );
}

function FormulaireAcces({ documents, onFerme, onCree }: {
  documents: { code: string; titre: string }[];
  onFerme: () => void;
  onCree: (r: { ref: string; lien: string }) => void;
}) {
  const { smqData } = useGed();
  const [intitule, setIntitule] = React.useState('');
  const [organisme, setOrganisme] = React.useState('');
  const [expireLe, setExpireLe] = React.useState('');
  const [choisis, setChoisis] = React.useState<string[]>([]);
  const [q, setQ] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);

  const filtres = React.useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return documents;
    return documents.filter((d) => `${d.code} ${d.titre}`.toLowerCase().includes(t));
  }, [documents, q]);

  const pret = intitule.trim() && expireLe && choisis.length > 0;

  return (
    <Modale titre="Ouvrir un accès auditeur" onClose={onFerme} largeur={660}
      pied={(
        <>
          <Bouton taille="sm" onClick={onFerme}>Annuler</Bouton>
          <Bouton variante="primaire" taille="sm" disabled={!pret || enCours} onClick={() => void (async () => {
              setEnCours(true);
              const r = await smqData<{ ref?: string; lien?: string }>('ged/acces-audits', {
                intitule: intitule.trim(), organisme: organisme.trim(),
                expireLe: new Date(`${expireLe}T23:59:59`).toISOString(), documents: choisis,
              });
              setEnCours(false);
              if (r?.ref && r?.lien) onCree({ ref: r.ref, lien: r.lien });
            })()}>
            {enCours ? 'Ouverture…' : `Ouvrir sur ${choisis.length} document(s)`}
          </Bouton>
        </>
      )}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={lbl}>Intitulé</label>
            <input value={intitule} onChange={(e) => setIntitule(e.target.value)} placeholder="Audit TUNAC — septembre 2026" style={inputStyle} />
          </div>
          <div>
            <label style={lbl}>Organisme</label>
            <input value={organisme} onChange={(e) => setOrganisme(e.target.value)} placeholder="TUNAC" style={inputStyle} />
          </div>
        </div>
        <div>
          <label style={lbl}>Dernier jour d’accès</label>
          <input type="date" value={expireLe} onChange={(e) => setExpireLe(e.target.value)} style={{ ...inputStyle, width: 190 }} />
          <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
            L’accès se referme tout seul ce soir-là. Au-delà de 60 jours, il est refusé : un accès long s’oublie.
          </p>
        </div>

        <div>
          <label style={lbl}>Documents consultables ({choisis.length} choisi(s))</label>
          {/* ⚠️ Une LISTE FERMÉE, pas un filtre enregistré. Un filtre continuerait
              de s'appliquer aux documents créés APRÈS la remise du lien :
              l'auditeur verrait ce qui est arrivé depuis, sans que personne
              l'ait décidé. */}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer la liste (code, titre)…" style={{ ...inputStyle, marginBottom: 7 }} />
          <div style={{ maxHeight: 230, overflowY: 'auto', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)' }}>
            {filtres.map((d) => {
              const on = choisis.includes(d.code);
              return (
                <button key={d.code} type="button"
                  onClick={() => setChoisis((c) => (on ? c.filter((x) => x !== d.code) : [...c, d.code]))}
                  aria-pressed={on}
                  style={{ display: 'grid', gridTemplateColumns: '20px 110px minmax(0,1fr)', gap: 9, alignItems: 'center', width: '100%', textAlign: 'start', padding: '7px 10px', border: 'none', borderTop: '1px solid var(--border-subtle)', background: on ? 'var(--cyan-50)' : 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
                  <span aria-hidden style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, borderRadius: 3, border: `1.5px solid ${on ? 'var(--cyan-600)' : 'var(--border-default)'}`, background: on ? 'var(--cyan-600)' : '#fff', color: '#fff' }}>
                    {on && <I n="check" s={10} sw={3} />}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600 }}>{d.code}</span>
                  <span style={{ fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.titre}</span>
                </button>
              );
            })}
            {filtres.length === 0 && (
              <p style={{ margin: 0, padding: '12px', fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun document ne correspond.</p>
            )}
          </div>
        </div>
      </div>
    </Modale>
  );
}

const lbl: React.CSSProperties = {
  display: 'block', fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5,
};
